<template>
  <div class="expression-input">
    <div
      ref="inputElement"
      class="expression-input__input"
      :class="{ '-error': state.errorCode != null }"
      @keydown="handleKeyDown"
      @pointerdown="onMouseDown"
      @copy="onCopy"
      @paste="onPaste"
    >
      <expression-input-node
        :node="state.tree.state.root"
        :level="0"
        :placeholder="expression.trim() == '' ? (defaultSign ? `${defaultSign}0` : '0') : ''"
        @click.prevent=""
        @focus-start="onFocusStart"
        @focus-end="onFocusEnd"
      />
      <template v-if="editor != 'none'">
        <div class="expression-input__expand-placeholder"></div>
        <button
          type="button"
          class="expression-input__expand"
          @pointerdown.prevent=""
          @click="openEditorPopup()"
        >
          <lucide-icon name="maximize-2" />
        </button>
      </template>
    </div>

    <div v-if="false">
      {{ state.tree.selection.startNode.type }}
      {{ state.tree.selection.startNode.id.slice(0, 6) }}
      {{ state.tree.selection.startOffset }} -
      {{ state.tree.selection.endNode.type }}
      {{ state.tree.selection.endNode.id.slice(0, 6) }}
      {{ state.tree.selection.endOffset }}
    </div>

    <transition name="expression-input__error-transition">
      <div v-if="state.errorCode != null && state.errorCode > 1" class="expression-input__error message-error mt-2">
        {{ state.errorMessage }}
      </div>
    </transition>

    <!-- Reference Popover -->
    <inline-popover
      v-if="itemPopover.visible"
      :left="itemPopover.left"
      :top="itemPopover.top"
      :items="elements"
      marker="@"
      @select="addItemReference"
      @close="closeItemPopover"
    />

    <!-- Variable Popover -->
    <inline-popover
      v-if="variablePopover.visible"
      :left="variablePopover.left"
      :top="variablePopover.top"
      :items="variables"
      marker="$"
      @select="addVariable"
      @close="closeVariablePopover"
    />

    <!-- Function Popover -->
    <inline-popover
      v-if="functionPopover.visible"
      :left="functionPopover.left"
      :top="functionPopover.top"
      :items="functions"
      marker="#"
      @select="addFunction"
      @close="closeFunctionPopover"
    />

    <expression-editor
      v-if="editor == 'inbuilt'"
      :visible="state.expressionEditorVisible"
      :tree="state.tree"
      :expression="expression"
      :parse-context="expressionParseContext"
      :input-mode="'expression'"
      @update:input-mode="emit('update:input-mode', $event)"
      @update:expression="onEditorExpressionUpdate"
      @close="closeEditor()"
    />
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, nextTick, onBeforeUnmount, provide, computed, watch, onMounted, toValue } from "vue";
import { useI18n } from "vue-i18n";
import { useElementBounding } from "@vueuse/core";
import { useToast } from "vue-toastification";

import LucideIcon from "@core/ui/LucideIcon.vue";

import ExpressionEditor from "./ExpressionEditor.vue";
import ExpressionInputNode from "./ExpressionInputNode.vue";
import InlinePopover from "@core/input/InlinePopover.vue";

import { useExpressionTree } from "./ExpressionTree";
import { useExpressionMenu } from "./useExpressionMenu";
import { useSettingsStore } from "@editor/store/settings.store";
import { useResourceStore } from "../resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useTargetStore } from "../target/targets.store";
import { useElements } from "../element/elements";
import { getRange } from "./RangeFunctions";
import { calculateExpression } from "./expressionFunctions";

import type { FunctionName } from "./expressionFunctions";
import type { Handleable } from "@core/types/Events";
import type { InputNode, NodeSelection } from "./ExpressionInputTypes";
import type { ExpressionParseContext } from "../effect/Expression";

defineOptions({
  name: "ExpressionInput"
});

const props = withDefaults(
  defineProps<{
    expression: string;
    parseContext: ExpressionParseContext;
    tree?: ReturnType<typeof useExpressionTree>;
    editor?: "inbuilt" | "external" | "none";
    defaultSign?: "+" | "-" | null;
    editorOpen?: boolean;
  }>(),
  {
    editorOpen: false,
    editor: "inbuilt",
    defaultSign: null
  }
);

const emit = defineEmits<{
  (e: "update:expression", value: string): void;
  (e: "update:input-mode", value: "expression" | "template" | "tree"): void;
  (e: "update:editor-open", value: boolean): void;
}>();

const settings = useSettingsStore();

const { t } = useI18n();
const inputElement = ref<HTMLElement>();

const elementBounding = useElementBounding(inputElement);

const targetStore = useTargetStore();
const indexStore = useIndexStore();
const resourceStore = useResourceStore();
const toast = useToast();

const { elements, variables, functions } = useExpressionMenu();
const elementValues = computed(() => useElements().elementValues);

const expressionParseContext = computed((): ExpressionParseContext => ({
  roundIndex: 0,
  targets: targetStore.items,
  indices: indexStore.items,
  resources: resourceStore.items,
  simulationSettings: {
    roundUnit: settings.simulationRoundUnit,
    roundLength: settings.simulationRoundLength,
    roundAmount: settings.simulationRoundAmount,
    timeStart: settings.simulationTimeStart,
    durationType: settings.simulationDurationType,
  },
  rounds: [{
    elementValues: elementValues.value,
    index: 0,
  }],
  ...props.parseContext
}));

const state = reactive({
  errorCode: null as null | number,
  errorMessage: "",
  expressionEditorVisible: props.editorOpen,
  tree: props.tree ?? useExpressionTree(expressionParseContext, props.expression),
  lastExpression: props.expression
});
watch(() => props.editorOpen, (value) => {
  state.expressionEditorVisible = value;
});

provide("parseContext", expressionParseContext);
provide("inputBounding", elementBounding);
provide("expressionTree", state.tree);

// Listen for changes in the expression prop
watch(() => props.expression, (value) => {
  if (value == state.lastExpression) return;
  state.lastExpression = value;
  state.tree.setExpression(value);
});

function handleKeyDown(event: KeyboardEvent) {
  // const range = getRange();
  const nodeSelection = state.tree.selection;

  const { startNode, endNode, endOffset } = nodeSelection;
  if (startNode == null || endNode == null) {
    console.log("No start or end node");
    return;
  }
  let nextFocusNode: InputNode = endNode;

  const key = event.key;

  // add an operator
  switch (key) {
    case "+":
    case "-":
    case "*":
    case "/":
    case "!":
    case "=":
    case ">":
    case "<": {
      event.preventDefault();
      return state.tree.insertOperator(key);
    }

    case "Backspace": {
      event.preventDefault();
      return state.tree.deleteBackwards();
    }

    case "#": {
      event.preventDefault();
      // open the function popover whenever the user presses #
      return openFunctionPopover();
    }

    case "@": {
      event.preventDefault();
      return openItemPopover();
    }

    case "$": {
      event.preventDefault();
      return openVariablePopover();
    }

    case "(": {
      event.preventDefault();
      return state.tree.insertParenthesis("opening");
    }

    case ")": {
      event.preventDefault();
      return state.tree.insertParenthesis("closing");
    }

    case "ArrowRight":
    case "ArrowLeft":
    case "ArrowUp":
    case "ArrowDown": {
      event.preventDefault();
      let nextOffset = endOffset || 0;

      if (key == "ArrowLeft" || key == "ArrowUp") {
        const prevSpot = state.tree.findPreviousCursorSpot(
          endNode,
          endOffset || 0
        );
        console.log("prevSpot", prevSpot);
        if (prevSpot) {
          nextFocusNode = prevSpot.node;
          nextOffset = prevSpot.startOffset;
        }
      } else {
        const nextSpot = state.tree.findNextCursorSpot(endNode, endOffset || 0);
        console.log("nextSpot", nextSpot);
        if (nextSpot) {
          nextFocusNode = nextSpot.node;
          nextOffset = nextSpot.startOffset;
        }
      }
      if (event.shiftKey) {
        onFocusEnd({ node: nextFocusNode, offset: nextOffset });
      } else {
        // If we have a selection chosen, we want to move the cursor to the
        // start or end of the selection, depending on the direction.
        const { startNode, endNode, startOffset, endOffset } =
          state.tree.orderSelection(nodeSelection);

        if (key == "ArrowLeft" || key == "ArrowUp") {
          if (startNode != endNode || startOffset != endOffset) {
            nextFocusNode = startNode;
            nextOffset = startOffset;
          }
        }
        if (key == "ArrowRight" || key == "ArrowDown") {
          if (startNode != endNode || startOffset != endOffset) {
            nextFocusNode = endNode;
            nextOffset = endOffset;
          }
        }
        onFocusStart({ node: nextFocusNode, offset: nextOffset });
      }

      return;
    }

    case "a":
    case "A":
      event.preventDefault();

      if (event.ctrlKey || event.metaKey) {
        state.tree.selectAll();
      }
      return;
    
    case "c":
    case "C":
      event.preventDefault();

      if (event.ctrlKey || event.metaKey) {
        onCopy();
        // const sel = window.getSelection();
        // if (sel) {
        //   const range = sel.getRangeAt(0);

        //   // text
        //   const text = range.toString();

        //   // html
        //   const div = document.createElement("div");
        //   div.appendChild(range.cloneContents());
        //   const html = div.innerHTML;
          
        //   const clipboardItem = new ClipboardItem({
        //     "text/plain": new Blob([text], { type: "text/plain" }),
        //     "text/html": new Blob([html], { type: "text/html" })
        //   });
        //   navigator.clipboard.write([clipboardItem]);
        // }
      }
      return;

    case "v":
    case "V":
      // Do not prevent default, so the paste event can be handled.
      return;

    case "&":
    case "Enter":
    case "Delete":
    case "Shift":
    case "Control":
    case "Alt":
    case "AltGraph":
    case "Meta":
      event.preventDefault();
      // do nothing
      return;
    
    case "z":
    case "Z":
      event.preventDefault();
      if (event.ctrlKey || event.metaKey) {
        state.tree.undo();
      }
      return;
    
    case "y":
    case "Y":
      event.preventDefault();
      if (event.ctrlKey || event.metaKey) {
        state.tree.redo();
      }
      return;
    
    case "Tab":
      // Do not prevent default, so the tab can be used to navigate.
      return;

    default: {
      event.preventDefault();
      
      if (/[0-9.,]+/.test(key)) {
        state.tree.insertCharacter(key);
        return;
      } else {
        console.log("PRESSED", key);
        return;
      }
    }
  }
}

// =============================================

function emitUpdate(expression?: string) {
  const treeExpression = state.tree.toExpressionString();
  if (expression != null && expression != treeExpression) {
    state.tree.setExpression(expression);
  }
  expression = expression ?? treeExpression;

  // validate input value
  try {
    // use the parser to check if the content generates any errors.
    parseValue(expression);
    setError(null);
  } catch (error) {
    console.log("expression", expression);
    // console.warn(error);
    setError(error as Error);
    return;
  }
  
  if (props.defaultSign && expression.trim() != "" && !expression.startsWith("tmpl_")) {
    const firstChar = expression.trim()[0];
    const oppositeSign = props.defaultSign == "+" ? "-" : "+";
    if (firstChar != props.defaultSign && firstChar != oppositeSign && firstChar != "=") {
      expression = props.defaultSign + expression;
    }
  }

  state.lastExpression = expression;

  emit("update:expression", expression);
}

function onEditorExpressionUpdate(newExpression: string) {
  // we need to handle direct updates from the editor
  // because template expression updates are not transmitted
  // through the expression tree.
  emitUpdate(newExpression);
}

/**
 * Try parsing the value with the expression parser.
 */
function parseValue(value: string): number | boolean {
  const calculated = calculateExpression(
    value.replace(/[\s]*/g, ""),
    expressionParseContext.value
  );
  return calculated.value;
}

const ERROR_CODES = {
  UNKNOWN_ERROR: 0,
  UNEXPECTED_END: 1,
  VALUE_EXPECTED: 2,
  OPERATOR_EXPECTED: 3,
  RECURSIVE: 4
};

function setError(error: Error | null) {
  if (error == null) {
    state.errorCode = null; // if parsing threw no error, the input is valid.
    state.errorMessage = "";
  } else {
    let errorMessage = error.message;
    console.warn(errorMessage);
    state.errorCode = ERROR_CODES.UNKNOWN_ERROR;
    if (errorMessage.includes(`"+", "-", or "="`)) {
      state.errorCode = ERROR_CODES.OPERATOR_EXPECTED;
      state.errorMessage = t("errors.expressionNeedsOperator");
    } else if (
      errorMessage.includes("Unexpected end") ||
      errorMessage.includes("Value expected")
    ) {
      state.errorCode = ERROR_CODES.UNEXPECTED_END;
      state.errorMessage = t("errors.expressionIncomplete");
    } else if (errorMessage.includes("Parenthesis )")) {
      state.errorMessage = t("errors.expressionMissingClosingParenthesis");
    } else if (errorMessage.includes("AssignmentNode")) {
      state.errorMessage = t("errors.expressionNeedsDoubleEquals");
    } else if (errorMessage.includes("Unexpected part")) {
      state.errorMessage = t("errors.expressionMissingOperator");
    } else if (errorMessage.includes("Value for x")) {
      state.errorMessage = t("errors.expressionCurrentValueUnavailable");
    } else if (errorMessage.includes("recursive")) {
      state.errorCode = ERROR_CODES.RECURSIVE;
      state.errorMessage = t("errors.expressionRecursive");
    } else if (errorMessage.includes("arguments")) {
      const regexResult = errorMessage.match(
        /Function ([a-zA-Z]+) requires ([0-9]+) arguments/
      );
      const functionCode = regexResult?.[1] || "";
      const argumentCount = regexResult?.[2] || "";
      const argCount = parseInt(argumentCount, 10);
      state.errorMessage = t(
        "errors.wrongFunctionArgumentsCount",
        {
          functionName: t(`expressionFunctions.${functionCode}`)
        },
        argCount
      );
    } else {
      state.errorMessage = errorMessage;
    }
  }
}

// =============================================
// EVENT HANDLING

function onSelectionChange() {
  if (state.expressionEditorVisible) return;

  if (mounted.value) {
    // Only select after mount to prevent grabbing focus greedily
    nextTick(() => {
      updateSelectionRange();
    });
  }
}

function onTreeUpdate() {
  emitUpdate();
}

state.tree.on("tree-updated", onTreeUpdate);
state.tree.on("selection-change", onSelectionChange);
onBeforeUnmount(() => {
  state.tree.off("selection-change", onSelectionChange);
  state.tree.off("tree-updated", onTreeUpdate);
});

// =============================================
// FOCUS HANDLING

function onFocusStart(focusEvent: { node: InputNode; offset: number }) {
  if (state.expressionEditorVisible) return;

  // console.warn("start", focusEvent);
  state.tree.updateSelection({
    startNode: focusEvent.node,
    startOffset: focusEvent.offset,
    endNode: focusEvent.node,
    endOffset: focusEvent.offset
  }, true);
}

function onFocusEnd(focusEvent: { node: InputNode; offset: number }) {
  if (state.expressionEditorVisible) return;

  state.tree.updateSelection({
    endNode: focusEvent.node,
    endOffset: focusEvent.offset
  }, true);
}

// function focusNode(node: InputNode, startOffset: number, endOffset?: number) {
//   onFocusStart({ node, offset: startOffset || 0 });
//   if (endOffset != null) {
//     onFocusEnd({ node, offset: endOffset || startOffset || 0 });
//   }
// }

/**
 * Select all text in the range.
 */
function updateSelectionRange(selection?: NodeSelection) {
  if (!selection) selection = state.tree.selection;

  const sel = window.getSelection();
  if (sel == null) return;

  let { node: startInputNode, startOffset } = state.tree.getFocussable(
    selection.startNode,
    selection.startOffset
  );
  let { node: endInputNode, startOffset: endOffset } = state.tree.getFocussable(
    selection.endNode,
    selection.endOffset,
    "left"
  );

  let startNode = inputElement.value?.querySelector(
    `[data-id="${startInputNode.id}"]`
  );
  let endNode = inputElement.value?.querySelector(
    `[data-id="${endInputNode.id}"]`
  );

  if (startNode == null || endNode == null) {
    console.warn(
      "could not find node",
      startInputNode?.type,
      endInputNode?.type
    );
    return;
  }

  // let swapped = false;

  // if the start node is after the end node, swap them
  if (startNode == endNode) {
    if (startOffset > endOffset) {
      // swapped = true;
      [startOffset, endOffset] = [endOffset, startOffset];
    }
  } else if (
    startNode.compareDocumentPosition(endNode) ===
    Node.DOCUMENT_POSITION_PRECEDING
  ) {
    // swapped = true;
    [startNode, endNode] = [endNode, startNode];
    [startOffset, endOffset] = [endOffset, startOffset];
  }

  let startContainer: Node = startNode;
  let endContainer: Node = endNode;

  if (startNode.classList.contains("expression-input-node--anchored")) {
    if (startOffset == 0) {
      startContainer = startNode.previousElementSibling as HTMLElement;
    } else {
      startContainer = startNode.nextElementSibling as HTMLElement;
    }
    startOffset = 0;
  }

  if (startContainer && startContainer.childNodes.length > 0) {
    startContainer = startContainer.childNodes[0];
  }

  if (endNode.classList.contains("expression-input-node--anchored")) {
    if (endOffset == 0) {
      endContainer = endNode.previousElementSibling as HTMLElement;
    } else {
      endContainer = endNode.nextElementSibling as HTMLElement;
    }
    endOffset = 0;
  }

  if (endContainer && endContainer.childNodes.length > 0) {
    endContainer = endContainer.childNodes[0];
  }

  if (startContainer == null || endContainer == null) {
    console.warn("could not find container");
    console.log(startContainer, startNode);
    console.log(endContainer, endNode);
    return;
  }

  sel.removeAllRanges();
  const range = document.createRange();
  range.setStart(startContainer, startOffset);
  range.setEnd(endContainer, endOffset);
  sel.addRange(range);
}

function getClosestEditableElement(event: MouseEvent) {
  const editableDivsList =
    inputElement.value?.querySelectorAll<HTMLElement>("[contenteditable]") ||
    [];

  const { x, y } = event;

  const allEditableDivs = Array.from(editableDivsList).filter(
    (div) => div.contentEditable
  );

  let closestNode: HTMLElement | null = null;
  let closestDistance = Infinity;

  for (const node of allEditableDivs) {
    const rect = node.getBoundingClientRect();
    const center = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };
    if (center == null) continue;
    const distance = Math.sqrt((center.x - x) ** 2 + (center.y - y) ** 2);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestNode = node;
    }
  }

  return closestNode;
}

function onMouseDown(event: MouseEvent & Handleable) {
  if (event.defaultPrevented) return;

  if (event.target && (event.target as HTMLElement).isContentEditable) {
    event.hasBeenHandled = true;
    return;
  }

  event.preventDefault();

  const inputNodeElement = getClosestEditableElement(event);
  if (inputNodeElement == null) {
    console.warn("no closest element");
    return;
  }

  const rect = inputNodeElement.getBoundingClientRect();
  const nodeCenter = {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2
  };

  let nodeId = inputNodeElement.dataset.id;

  if (nodeId == null) {
    nodeId = inputNodeElement?.dataset.connectedId;
  }

  if (nodeId == null) {
    console.warn("could not find node id");
    return;
  }

  const node = state.tree.findNode(nodeId);

  if (node == null) {
    console.warn("could not find node", nodeId);
    return;
  }

  if (event.x < (nodeCenter?.x || 0)) {
    state.tree.updateSelection({
      startNode: node.node,
      startOffset: 0,
      endNode: node.node,
      endOffset: 0
    }, true);
  } else {
    const maxOffset = state.tree.getMaxOffset(node.node);
    state.tree.updateSelection({
      startNode: node.node,
      startOffset: maxOffset,
      endNode: node.node,
      endOffset: maxOffset
    }, true);
  }
}

function focus() {
  state.tree.selectAll();
  updateSelectionRange();
}

defineExpose({
  focus,
  updateSelectionRange
});


// =============================================
// COPY & PASTE HANDLING

function onCopy(event?: ClipboardEvent) {
  const sel = window.getSelection();
  if (sel) {
    const range = sel.getRangeAt(0);
    
    const partialExpression = expressionFromRange(range);
    console.log("partialExpression", partialExpression);

    // text
    const text = range.toString();

    // html
    const div = document.createElement("div");
    div.appendChild(range.cloneContents());
    div.className = "copied-simcision-expression";
    div.setAttribute("data-expression", partialExpression);
    const html = div.outerHTML;

    if (event) {
      event.clipboardData?.setData("text/plain", text);
      event.clipboardData?.setData("text/html", html);
      event.preventDefault();
    } else {
      navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([text], { type: "text/plain" }),
          "text/html": new Blob([html], { type: "text/html" })
        })
      ]);
    }
  }
}

function onPaste(event: ClipboardEvent) {
  const clipboardData = event.clipboardData;
  if (!clipboardData) return;
  
  event.preventDefault();

  const html = clipboardData.getData("text/html");
  const text = clipboardData.getData("text/plain");

  if (html) {
    const div = document.createElement("div");
    div.innerHTML = html;
    const expressionAttr = div.querySelector(".copied-simcision-expression")?.getAttribute("data-expression");
    if (expressionAttr) {
      const subTree = useExpressionTree(expressionParseContext, expressionAttr);
      if (subTree.state.parseError != null) {
        toast.error(t("errors.couldNotParseExpression", { expression: expressionAttr }));
        console.warn("could not parse pasted expression", expressionAttr, subTree.state.parseError);
        return;
      }
      state.tree.insertNode(subTree.state.root);
      return;
    }
  }

  if (text) {
    console.log("pasting text", text);
    try {
      const subTree = useExpressionTree(expressionParseContext, text.trim());
      if (subTree.state.parseError == null) {
        state.tree.insertNode(subTree.state.root);
        return;
      } else {
        console.warn("could not parse pasted text as expression", text, subTree.state.parseError);
      }
    } catch (error) {
      console.warn("could not parse pasted text as expression", text, error);
    }
    const parsed = parseFloat(text);
    if (parsed == null || isNaN(parsed as number) || parsed === Infinity || parsed === -Infinity) {
      console.warn("could not parse pasted text", text);
      toast.error(t("errors.couldNotParseExpression", { expression: text }));
      return;
    }
    const value = "" + parsed || "";
    state.tree.insertCharacter(value);
  }
}

function expressionFromRange(range: Range): string {
  const div = document.createElement("div");
  div.appendChild(range.cloneContents());
  const html = div.innerHTML;
  
  if (!html.includes("<")) {
    // if the html is just text, we can just return it as is.
    return html.replaceAll(",", ".") + " ";
  }
  const parsedHTML = new DOMParser().parseFromString(html, "text/html");
  let partialExpression = "";

  // step through the parsed html tree, find the corresponding nodes in the expression tree,
  // and build the expression string from those nodes.
  const traverse = (node: Node, parentInputNode: null | InputNode) => {
    if (node.nodeType != Node.ELEMENT_NODE) return;
    const element = node as HTMLElement;

    const nodeId = element.dataset.id;
    const inputNode = nodeId == null ? null : state.tree.findNode(nodeId);

    let childrenHandled = false;

    if (inputNode) {
      if (inputNode.node.type === "constant") {
        partialExpression += (element.textContent || "").replaceAll(",", ".");
        partialExpression += " ";
      } else if (inputNode.node.type === "item") {
        const uid = toValue(inputNode.node.item)?.uid;
        if (uid == null) {
          console.warn("could not find item uid for node", inputNode.node);
        } else {
          partialExpression += uid;
        }
      } else if (inputNode.node.type === "variable") {
        partialExpression += inputNode.node.code;
      } else if (inputNode.node.type === "function") {
        partialExpression += inputNode.node.name + "(";
        node.childNodes.forEach(childNode => traverse(childNode, inputNode.node));
        partialExpression += ")";
        childrenHandled = true;
      } else if (inputNode.node.type === "parenthesis") {
        partialExpression += "(";
        node.childNodes.forEach(childNode => traverse(childNode, inputNode.node));
        partialExpression += ")";
        childrenHandled = true;
      }
    }

    if (element.classList.contains("expression-input-node__operator")) {
      partialExpression += element.textContent || "";
    }
    if (element.classList.contains("expression-input-node__functionseparator")) {
      partialExpression += ",";
    }

    if (!childrenHandled) {
      node.childNodes.forEach(childNode => traverse(childNode, parentInputNode));
    }
  };
  parsedHTML.body.childNodes.forEach(childNode => traverse(childNode, null));

  return partialExpression;
}

// =============================================
// EXPRESSION EDITOR POPUP

function openEditorPopup() {
  if (props.editor == "inbuilt") {
    state.expressionEditorVisible = true;
  }
  emit("update:editor-open", true);
}

function closeEditor() {
  emit("update:editor-open", false);
  
  state.tree.setInsertMode("fine");
  if (props.editor == "inbuilt") {
    state.expressionEditorVisible = false;
  }

  updateSelectionRange();
}

const mounted = ref(false);
onMounted(() => {
  mounted.value = true;
});

// =============================================
// ITEM POPOVER

function addItemReference(item: { uid: string }) {
  return state.tree.insertItem(item.uid);
}

const itemPopover = reactive({
  visible: false,
  top: 0,
  left: 0,
  range: null as null | Range
});

function openItemPopover() {
  let left = 0;
  let top = 0;

  const range = getRange();

  if (range) {
    const rect =
      range.startContainer.nodeType === Node.TEXT_NODE
        ? range.getBoundingClientRect()
        : range.startContainer instanceof HTMLElement
          ? range.startContainer.getBoundingClientRect()
          : range.getBoundingClientRect();

    top = rect.top;
    left = rect.left;

    // prevent invalid value
    if (top === 0) {
      const parentElement = range.startContainer.parentElement;
      if (parentElement) {
        const newRect = parentElement.getBoundingClientRect();
        top = newRect.top;
        left = newRect.left;
      }
    }
  }

  itemPopover.range = range;
  itemPopover.top = top;
  itemPopover.left = left;
  itemPopover.visible = true;
}

function closeItemPopover(resetFocus = true) {
  itemPopover.visible = false;

  // restore caret position
  if (resetFocus) {
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      if (itemPopover.range) {
        sel.addRange(itemPopover.range);
      }
    }
  }
}

// =============================================
// VARIABLE POPOVER

function addVariable(variableData: { code: string }) {
  state.tree.insertVariable(variableData.code);
}

const variablePopover = reactive({
  visible: false,
  top: 0,
  left: 0,
  range: null as null | Range
});

function openVariablePopover() {
  let left = 0;
  let top = 0;

  const range = getRange();

  if (range) {
    const rect =
      range.startContainer.nodeType === Node.TEXT_NODE
        ? range.getBoundingClientRect()
        : range.startContainer instanceof HTMLElement
          ? range.startContainer.getBoundingClientRect()
          : range.getBoundingClientRect();

    top = rect.top;
    left = rect.left;

    // prevent invalid value
    if (top === 0) {
      const parentElement = range.startContainer.parentElement;
      if (parentElement) {
        const newRect = parentElement.getBoundingClientRect();
        top = newRect.top;
        left = newRect.left;
      }
    }
  }

  variablePopover.range = range;
  variablePopover.top = top;
  variablePopover.left = left;
  variablePopover.visible = true;
}

function closeVariablePopover(resetFocus = true) {
  variablePopover.visible = false;

  // restore caret position
  if (resetFocus) {
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      if (variablePopover.range) {
        sel.addRange(variablePopover.range);
      }
    }
  }
}

// =============================================
// FUNCTION POPOVER

/**
 * Insert a function reference element into the input.
 */
function addFunction(functionData: { code: FunctionName }) {
  return state.tree.insertFunction(functionData.code);
}

const functionPopover = reactive({
  visible: false,
  top: 0,
  left: 0,
  range: null as null | Range
});

/**
 * Show an overlay that can be used to search for a
 * function to reference in the expression.
 * @param {Range?} range
 */
function openFunctionPopover() {
  let left = 0;
  let top = 0;

  const range = getRange();

  if (range) {
    const rect =
      range.startContainer.nodeType === Node.TEXT_NODE
        ? range.getBoundingClientRect()
        : range.startContainer instanceof HTMLElement
          ? range.startContainer.getBoundingClientRect()
          : range.getBoundingClientRect();

    top = rect.top;
    left = rect.left;

    // prevent invalid value
    if (top === 0) {
      const parentElement = range.startContainer.parentElement;
      if (parentElement) {
        const newRect = parentElement.getBoundingClientRect();
        top = newRect.top;
        left = newRect.left;
      }
    }
  }

  functionPopover.range = range;
  functionPopover.top = top;
  functionPopover.left = left;
  functionPopover.visible = true;
}

function closeFunctionPopover(resetFocus = true) {
  functionPopover.visible = false;

  // restore caret position
  if (resetFocus) {
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      if (functionPopover.range) {
        sel.addRange(functionPopover.range);
      }
    }
  }
}
</script>

<style lang="scss">
.expression-input {
  /* Add styles for the container element */
  width: 100%;
  position: relative;

  &__input {
    width: 100%;
    position: relative;
    /* Add styles for the input element */
    display: flex;
    flex-wrap: wrap;
    min-height: 1.5rem;
    border: 1px solid var(--color-border);
    padding: 0.5rem;
    border-radius: 0.25rem;
    outline: 1px solid transparent;
    transition: outline 150ms ease-out, border-color 150ms ease-out;

    &:hover {
      outline-color: var(--color-border);
    }

    &:focus,
    &:focus-within {
      outline-color: var(--color-action);
      border-color: var(--color-action);
    }

    &.-error {
      border-color: var(--color-error);
      outline-color: var(--color-error);
    }
  }

  &__error {
    position: absolute;
    top: 100%;
    left: 0;
    width: max-content;
    max-width: 20rem;
    z-index: 1;
    box-shadow: 0 3px 10px var(--color-shadow-3);
  }
  &__error-transition-enter-active,
  &__error-transition-leave-active {
    transition: opacity 250ms ease-out, transform 250ms ease-out;
  }
  &__error-transition-enter-from,
  &__error-transition-leave-to {
    opacity: 0;
  }
  &__error-transition-enter-from {
    transform: translateY(1rem);
  }

  &__expand {
    background-color: var(--color-background-deep);
    color: var(--color-text-light);
    border: none;
    margin-left: -3px;
    position: absolute;
    right: 0;
    bottom: 0;
    height: 1.75rem;
    width: 1.75rem;
    padding: 0;
    text-align: center;
    border-left: 1px solid var(--color-border-light);
    border-top: 1px solid var(--color-border-light);
    border-bottom-right-radius: inherit;

    &:hover {
      color: var(--color-action);
    }
    &:focus-visible {
      color: var(--color-text);
      border-color: var(--color-text);
      outline: 2px dashed var(--color-action);
      outline-offset: 4px;
    }
    &:active {
      background-color: var(--color-action);
      color: var(--color-action-contrast);
    }

    &-placeholder {
      height: 1.8rem;
      width: 2.5rem;
    }
  }
}
</style>
./expressionFunctions